import {
  createRouter,
  createRootRoute,
  createRoute,
  redirect,
  type ParsedLocation,
} from '@tanstack/react-router';
import { Layout } from './components/layout.js';
import { AppErrorScreen } from './components/app-error.js';
import { AppNotFoundScreen } from './components/app-not-found.js';
import { LoginPage } from './routes/login.js';
import { AuditPage } from './routes/audit.js';
import { ApprovalsPage } from './routes/approvals.js';
import { ZakazkyPage } from './routes/zakazky.js';
import { InzeratPage, InzeratPickerPage } from './routes/inzerat.js';
import { KlientiPage } from './routes/klienti.js';
import { PoptavkyPage } from './routes/poptavky.js';
import { UkolyPage } from './routes/ukoly.js';
import { KalendarPage } from './routes/kalendar.js';
import { ProhlidkaPage } from './routes/prohlidka.js';
import { ProhlidkyPage } from './routes/prohlidky.js';
import { ParovaniPoptavekPage } from './routes/parovani-poptavek.js';
import { NakladyPage } from './routes/naklady.js';
import { NakladyInzercePage } from './routes/naklady-inzerce.js';
import { ProjektyPage } from './routes/projekty.js';
import { NeprirazeneNakladyPage } from './routes/neprirazene-naklady.js';
import { EkonomikaKancelarePage } from './routes/ekonomika-kancelare.js';
import { KampanePage } from './routes/kampane.js';
import { AkvizicePage } from './routes/akvizice.js';
import { NaborPage } from './routes/nabor.js';
import { AdministracePage } from './routes/administrace.js';
import { ObjednavkyFrontaPage } from './routes/objednavky-fronta.js';
import { KDoplneniPage } from './routes/k-doplneni.js';
import { NastaveniPage } from './routes/nastaveni.js';
import { CoChystamePage } from './routes/co-chystame.js';
import { NapovedaPage } from './routes/napoveda.js';
import { TarifPage } from './routes/tarif.js';
import { AiSuperadminPage } from './routes/ai-superadmin.js';
import { ProvozSuperadminPage } from './routes/provoz-superadmin.js';
import { ResetPasswordRequestPage } from './routes/reset-password.js';
import { ResetPasswordConfirmPage } from './routes/reset-password-confirm.js';
import { InvitationAcceptPage } from './routes/invitation-accept.js';
import { DnesekPage } from './routes/dnesek.js';
import { ZakazkaDetailPage } from './routes/zakazka-detail.js';
import { isCaseTabId } from './lib/texts.js';
import { isPreparingPathHidden } from './lib/nav.js';
import { NemovitostDetailPage } from './routes/nemovitost-detail.js';
import { NemovitostiPage } from './routes/nemovitosti.js';
import { PortalyPage } from './routes/portaly.js';
/** Krok QAFIX2 (nalez N03): pruvodce prevodu kancelare z jineho softwaru. */
import { ImportPage } from './routes/import.js';
import { PreparingPage } from './routes/preparing.js';
import { FakturacePage } from './routes/fakturace.js';
import { SpravaBytuPage } from './routes/sprava-bytu.js';
import { SmlouvyPage } from './routes/smlouvy.js';
import { ContractsScreen } from './components/contracts.js';
import { SmlouvaDetailPage } from './routes/smlouva-detail.js';
import { SmlouvaNovaPage } from './routes/smlouva-nova.js';
import type { EditorTabId } from './components/contracts.js';
import { SmlouvySablonyPage } from './routes/smlouvy-sablony.js';
import { ElektronickePodpisyPage } from './routes/elektronicke-podpisy.js';
import { ReportyPage } from './routes/reporty.js';
import { AsistentkaPage } from './routes/asistentka.js';
import { NovaZakazkaPage } from './routes/zakazka-novy.js';
import { ensureSession, useSession } from './stores/session.js';
import { t } from '@reos/i18n';

/**
 * Sekce bez obsahu vedou na stranku PRIPRAVUJE SE, ne na 404 (BLOK 2 ukol 2,
 * rozsireno BLOK 2.5 na 9sekcni IA — docs/platform/ui/NAVIGACE-A-IKONY.md).
 */
const PREPARING_PATHS = [
  '/pozornost',
  '/nabidky-rezervace',
  '/publikace',
  '/statistiky-inzerce',
  '/ohlasy-portalu',
  '/aukce',
  '/web-kancelare',
  '/microsite',
  '/vizitka-maklere',
  '/weby-projektu',
  '/klientsky-portal',
  '/schranka',
  '/mailing',
  '/sms',
  '/notifikace',
  '/emailove-schranky',
  '/dokumenty',
  '/naklady-nemovitosti',
  '/provize',
  '/banka-platby',
  '/statistiky',
  '/trh-srovnani',
  '/ocenovani',
  '/zdroje-klientu',
  '/uzivatele-role',
  '/pobocky-tymy',
  '/zdroje',
  '/import-migrace',
  '/gdpr',
  '/zalohy-provoz',
  '/domeny-hosting',
] as const;

/**
 * Krok S3: KAZDA obrazovka produktu je za prihlasenim. Verejne zustavaji jen
 * `/login`, `/reset-password*` a `/invitation/accept` — tam se uzivatel jeste
 * prihlasit nemuze. Neprihlaseny pozadavek konci na `/login?return_to=<cesta>`,
 * takze po prihlaseni pokracuje tam, kam sel.
 */
async function requireAuth({ location }: { location: ParsedLocation }): Promise<void> {
  const check = await ensureSession();
  if (check.status === 'authenticated') return;
  /*
   * Krok E2E7: NEDOSTUPNY SERVER NENI ODHLASENI.
   *
   * Kdyz `GET /api/me` skonci 502 nebo sitovou chybou (preview API restartoval
   * pm2 na stropu pameti — REPORT-step-E2E7.md), nevime, jestli session plati,
   * takze uzivatele NESMIME poslat na `/login`: jeho cookie dal plati a na
   * prihlasovaci obrazovce by s nim stejne neuspel, protoze server neodpovida
   * (a do demo kancelare se bez hesla uz nevrati). Misto toho vyhodime chybu,
   * kterou zachyti ceska `AppErrorScreen` s „Zkusit znovu" — session zustava
   * ve stavu `unknown`, takze dalsi pokus se serveru zepta znovu.
   */
  if (check.status === 'unavailable') {
    throw new Error(t('webAdmin.appError.serverUnavailable', { locale: 'cs' }));
  }
  throw redirect({ to: '/login', search: { return_to: location.href } });
}

/**
 * Krok O1 + UX1: stranka PRIPRAVUJE SE, kterou v tomto stavu NEVIDIME, se
 * presmeruje na Dnesek — stejne jako ji skryje menu (`nav.ts buildNav`).
 * Rozhoduje `isPreparingPathHidden`, tedy stejna logika jako v menu: flag
 * `pilot.menu` schová vsechny preparing polozky, `pilot.showHiddenSections`
 * (krok UX1) zase odemyka skupinu „Skrýt do napojení“. Flagy jsou uz v
 * session store (ensureSession), ctu se tu bez dalsiho volani API.
 *
 * ROZHODNUTI: cil je '/prehled' (skutecna Dnesek route od kroku C2), ne
 * redirect shim '/' nebo '/dnesek' — ty maji netypovanou `to: string`,
 * takze by neprosly typovanou kontrolou `redirect()`.
 */
async function requireAuthAndSkipPreparing(opts: { location: ParsedLocation }): Promise<void> {
  await requireAuth(opts);
  const state = useSession.getState();
  if (
    isPreparingPathHidden(opts.location.pathname, {
      pilotMenu: state.pilotMenu,
      showHiddenSections: state.showHiddenSections,
    })
  ) {
    throw redirect({ to: '/prehled' });
  }
}

const rootRoute = createRootRoute({
  component: Layout,
});

/**
 * `/prehled` = Dnesek (vstupni obrazovka administrace). ROZHODNUTI S3
 * (`REPORT-step-S3.md`) urcilo obsah, krok C2 presunul cestu z `/` na
 * `/prehled` — koren domeny patri VEREJNE strance (apps/web-public),
 * administrace zacina az za `/prehled`.
 */
const prehledRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/prehled',
  beforeLoad: requireAuth,
  component: DnesekPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>): { return_to?: string; reset?: string } => ({
    ...(typeof search['return_to'] === 'string' ? { return_to: search['return_to'] } : {}),
    ...(typeof search['reset'] === 'string' ? { reset: search['reset'] } : {}),
  }),
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordRequestPage,
});

const resetPasswordConfirmRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password/confirm',
  component: ResetPasswordConfirmPage,
});

const invitationAcceptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invitation/accept',
  component: InvitationAcceptPage,
});

const auditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/audit',
  beforeLoad: requireAuth,
  component: AuditPage,
});

const approvalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/schvaleni',
  beforeLoad: requireAuth,
  component: ApprovalsPage,
});

const zakazkyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/zakazky',
  beforeLoad: requireAuth,
  component: ZakazkyPage,
});

/**
 * Krok UXR2: vybrany klient je v URL (`?klient=<id>`), ne jen ve stavu
 * komponenty — predloha `04-klienti.html` ma detail klienta vedle seznamu a
 * karta „Lidé" v detailu zakazky na nej odkazuje. Neplatna hodnota se
 * zahodi a obrazovka otevre jen seznam.
 */
function validateKlientiSearch(search: Record<string, unknown>): { klient?: string } {
  const value = search['klient'];
  return typeof value === 'string' && value !== '' ? { klient: value } : {};
}

const klientiRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/klienti',
  beforeLoad: requireAuth,
  validateSearch: validateKlientiSearch,
  component: KlientiPage,
});

// Krok CRM1: pipeline poptavek a zajemcu (drive PREPARING_PATHS - viz vyse).
const poptavkyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/poptavky',
  beforeLoad: requireAuth,
  component: PoptavkyPage,
});

// Krok CRM2: tabule ukolu maklere (drive PREPARING_PATHS - viz vyse).
const ukolyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ukoly',
  beforeLoad: requireAuth,
  component: UkolyPage,
});

// Krok CRM3: kalendar maklere - den/týden/měsíc/seznam, zdroje = makleři
// kanceláře, přetahování i natažení termínu, export iCal (drive
// PREPARING_PATHS - viz vyse).
const kalendarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/kalendar',
  beforeLoad: requireAuth,
  component: KalendarPage,
});

const nakladyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/naklady',
  beforeLoad: requireAuth,
  component: NakladyPage,
});

// Krok O3: obe cesty byly do ted PRIPRAVUJE SE (PREPARING_PATHS vyse).
const nakladyInzerceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/naklady-inzerce',
  beforeLoad: requireAuth,
  component: NakladyInzercePage,
});

// Krok DEV1: developerske projekty (projekt -> jednotky -> cenik -> export).
const projektyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/projekty',
  beforeLoad: requireAuth,
  component: ProjektyPage,
});

const neprirazeneNakladyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/neprirazene-naklady',
  beforeLoad: requireAuth,
  component: NeprirazeneNakladyPage,
});

// Krok FAK1: '/fakturace' bylo do ted PRIPRAVUJE SE (PREPARING_PATHS vyse).
const fakturaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/fakturace',
  beforeLoad: requireAuth,
  component: FakturacePage,
});

// Krok FAK3: '/sprava-bytu' bylo do ted PRIPRAVUJE SE (PREPARING_PATHS vyse).
const spravaBytuRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sprava-bytu',
  beforeLoad: requireAuth,
  component: SpravaBytuPage,
});

// Krok AI9: '/ekonomika-kancelare' bylo do ted PRIPRAVUJE SE (PREPARING_PATHS vyse).
const ekonomikaKancelareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ekonomika-kancelare',
  beforeLoad: requireAuth,
  component: EkonomikaKancelarePage,
});

// Krok T3: '/smlouvy' je nova polozka menu Provoz; '/smlouvy-sablony' bylo
// do ted PRIPRAVUJE SE (PREPARING_PATHS vyse).
const smlouvyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/smlouvy',
  beforeLoad: requireAuth,
  component: SmlouvyPage,
});

/**
 * Krok UXS1 bod 1: editor smlouvy ma VLASTNI ADRESU.
 *
 * `/smlouvy` je prehled, `/smlouvy/:id` editor jedne smlouvy. Do ted „Otevřít"
 * jen prepnulo vnitrni stav obrazovky, takze Zpet v prohlizeci odvedlo mimo
 * smlouvy a odkaz na konkretni smlouvu nesel poslat. `/smlouvy/nova` je
 * registrovana PRED `/smlouvy/$id`, aby ji dynamicka cesta neprebrala
 * (stejne jako `/zakazky/novy` vs. `/zakazky/$id`).
 */
const smlouvaNovaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/smlouvy/nova',
  beforeLoad: requireAuth,
  validateSearch: (search: Record<string, unknown>): { zakazka?: string } =>
    typeof search['zakazka'] === 'string' ? { zakazka: search['zakazka'] } : {},
  component: function SmlouvaNovaRoute() {
    const { zakazka } = smlouvaNovaRoute.useSearch();
    return <SmlouvaNovaPage {...(zakazka !== undefined ? { caseId: zakazka } : {})} />;
  },
});

/** Zalozka smlouvy v URL (`?zalozka=podpis`); neplatna hodnota se zahodi. */
function validateContractSearch(search: Record<string, unknown>): { zalozka?: EditorTabId } {
  const value = search['zalozka'];
  return value === 'editor' || value === 'signature' || value === 'client'
    ? { zalozka: value }
    : {};
}

const smlouvaDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/smlouvy/$id',
  beforeLoad: requireAuth,
  validateSearch: validateContractSearch,
  component: function SmlouvaDetailRoute() {
    const { id } = smlouvaDetailRoute.useParams();
    const { zalozka } = smlouvaDetailRoute.useSearch();
    return <SmlouvaDetailPage contractId={id} {...(zalozka !== undefined ? { tab: zalozka } : {})} />;
  },
});

const smlouvySablonyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/smlouvy-sablony',
  beforeLoad: requireAuth,
  component: SmlouvySablonyPage,
});

// Krok T5 bod 3: sekce DOKUMENTY ma podle mockupu ctyri polozky. Obe cesty
// byly do ted PRIPRAVUJE SE (PREPARING_PATHS vyse).
const elektronickePodpisyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/elektronicke-podpisy',
  beforeLoad: requireAuth,
  component: ElektronickePodpisyPage,
});

const reportyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reporty',
  beforeLoad: requireAuth,
  component: ReportyPage,
});

// Cesta '/admin' - MENU_DEFINITIONS (capabilities.ts) uz na ni odkazovala
// pred R5 (polozka 'Administrace'), jen bez registrovane route (viz REPORT).
const administraceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  beforeLoad: requireAuth,
  component: AdministracePage,
});

const nemovitostiListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/nemovitosti',
  beforeLoad: requireAuth,
  component: NemovitostiPage,
});

// Krok C3: fronta objednavek reportu (NEEDS_REVIEW / attention) - "dorucit i
// tak" a refundace, stejny guard jako mark-paid/refund (owner/admin).
const objednavkyFrontaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/objednavky-fronta',
  beforeLoad: requireAuth,
  component: ObjednavkyFrontaPage,
});

// Krok H1: fronta „K doplneni" - otazky, na ktere server odpoved nenasel
// a prebira je clovek (rozhodnuti R.B. 26. 9. 2026).
const kDoplneniRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/k-doplneni',
  beforeLoad: requireAuth,
  component: KDoplneniPage,
});

// Krok UXA1: „Nastavení" (avatar menu) + „Co chystáme" (seznam nehotovych
// sekci, link z Nastaveni) — ani jedna uz neni PREPARING_PATHS.
//
// Krok UXL1 bod 1: zalozka je v URL (`?tab=`), aby na ni slo odkazovat z
// jinych modulu (napr. Fakturace -> Kancelar). Neplatna/chybejici hodnota
// se zahodi — stranka si dopocita vychozi zalozku sama.
function validateNastaveniSearch(search: Record<string, unknown>): { tab?: string } {
  const value = search['tab'];
  return typeof value === 'string' && value !== '' ? { tab: value } : {};
}

const nastaveniRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/nastaveni',
  beforeLoad: requireAuth,
  validateSearch: validateNastaveniSearch,
  component: NastaveniPage,
});

// Krok AS3 bod 5: plocha „Asistentka" (stav placeni AI, ranni prehled,
// ukoly, na koho cekame) — proxy na core pres adminí backend.
const asistentkaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/asistentka',
  beforeLoad: requireAuth,
  component: AsistentkaPage,
});

/**
 * Krok APP2 bod 3 (bod 21 zadani) — NAPOVEDA V APLIKACI jako jedna stranka.
 * Odkaz na ni je v levem menu (`HELP_ITEM` v `lib/nav.ts`) i v Ctrl+K.
 */
const napovedaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/napoveda',
  beforeLoad: requireAuth,
  component: NapovedaPage,
});

/**
 * Krok APP2 bod 3 — `/tym` nema vlastni obrazovku a mit ji nema: tym se
 * spravuje KARTOU v Nastaveni (`TeamCard` na zalozce Kancelář, krok QAFIX4b
 * bod 2; sesta zalozka by znamenala zmenu chraneneho meridla
 * `nastaveni.spec.tsx`). Cesta proto vede tam, kde se to opravdu dela —
 * stejny vzor jako ostatni redirecty niz, jen s zalozkou v URL.
 */
const tymRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tym',
  beforeLoad: () => {
    throw redirect({ to: '/nastaveni', search: { tab: 'kancelar' } });
  },
});

const coChystameRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/co-chystame',
  beforeLoad: requireAuth,
  component: CoChystamePage,
});

/**
 * Krok QAFIX9 (nalez QA-ZAV-N1) — „Tarif a platba" UVNITR aplikace. Do
 * QAFIX9 vedlo „Objednat tarif" na verejny `/cenik` -> `/aktivace`, tedy na
 * registraci NOVE kancelare. Vchody jsou karta „Vas tarif" a pruh konce
 * zkusebniho obdobi (`trial.payUrl`), NE levé menu: `lib/nav.ts` a
 * `nav.spec` jsou chranene meridlo na pocet polozek.
 */
const tarifRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tarif',
  beforeLoad: requireAuth,
  component: TarifPage,
});

/**
 * Krok CRM4: veřejná stránka výběru termínu prohlídky (`/prohlidka/<token>`).
 * ZADNY `requireAuth` — zájemce nemá účet, autorizuje token z odkazu (server
 * vrací 404/410). Shell administrace se tu nekreslí (`isPublicClientPath`).
 */
const prohlidkaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/prohlidka/$token',
  component: function ProhlidkaRoute() {
    const { token } = prohlidkaRoute.useParams();
    return <ProhlidkaPage token={token} />;
  },
});

/** Krok CRM4: přehled prohlídek (dříve PŘIPRAVUJE SE). */
const prohlidkyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/prohlidky',
  beforeLoad: requireAuth,
  component: ProhlidkyPage,
});

/** Krok CRM4: nové shody poptávek s nabídkami (dříve PŘIPRAVUJE SE). */
const parovaniPoptavekRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/parovani-poptavek',
  beforeLoad: requireAuth,
  component: ParovaniPoptavekPage,
});

/**
 * Krok KR1 bod 6 — `/interni/ai-superadmin`. ZADNY `requireAuth`: neni to
 * tenantni stranka (R.B. ji muze otevrit i bez prihlasene kancelare),
 * autorizace je token v hlavicce (viz `routes/ai-superadmin.tsx`).
 */
const aiSuperadminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/interni/ai-superadmin',
  component: AiSuperadminPage,
});

/**
 * Krok D6b body 2 a 3 — `/interni/provoz` (naklad Kompletu + funnel).
 * Stejny duvod bez `requireAuth` jako u `/interni/ai-superadmin`: neni to
 * tenantni stranka, autorizace je token v hlavicce.
 */
const provozSuperadminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/interni/provoz',
  component: ProvozSuperadminPage,
});

const nemovitostDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/nemovitosti/$id',
  beforeLoad: requireAuth,
  component: NemovitostDetailPage,
});

/**
 * Aktivni sekce detailu v URL (Milnik 1, bod 5). Neplatna hodnota se
 * NEPROPAGUJE do komponenty — validateSearch ji zahodi a detail otevre
 * vychozi Kokpit. Diky search kontraktu funguje back/forward i reload.
 */
function validateCaseSearch(search: Record<string, unknown>): { sekce?: string; smlouva?: string } {
  const value = search['sekce'];
  const contract = search['smlouva'];
  return {
    ...(isCaseTabId(value) ? { sekce: value as string } : {}),
    // Krok UXS1 bod 1: stary odkaz na konkretni smlouvu uvnitr zakazky
    // (`?sekce=smlouva&smlouva=<id>`) se presmeruje na `/smlouvy/<id>` —
    // parametr proto musi projit validaci, aby ho `beforeLoad` videl.
    ...(typeof contract === 'string' && contract !== '' ? { smlouva: contract } : {}),
  };
}

/**
 * Pruvodce Nova zakazka. Registrovan PRED `/zakazky/$id`, aby ho neprebral.
 *
 * Krok GPS1: `?lat=…&lon=…` predvyplni polohu (kolega posle odkaz na
 * pozemek). `validateSearch` je zamerne identita, stejne jako u `/mapa` —
 * rozklad parametru dela `parsePlaceLink` az v komponente, aby se o autoritu
 * (jaky parametr je souradnice) nerozhodovalo na dvou mistech.
 */
const zakazkyNovyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/zakazky/novy',
  validateSearch: (search: Record<string, unknown>) => search,
  beforeLoad: requireAuth,
  component: NovaZakazkaPage,
});

// Krok UXV4c: nabor na miste (`?caseId=&eventId=&address=` z kalendare / „Nová").
const naborRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/nabor',
  validateSearch: (search: Record<string, unknown>) => search,
  beforeLoad: requireAuth,
  component: NaborPage,
});

/**
 * Krok UXV4b — Nový inzerát z proverení zakazky (dva kroky, rezim soustredeni).
 * `/inzerat` = vyber zakazky z „Nová"; `/zakazky/$id/inzerat` = samotny inzerat.
 */
const inzeratPickerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inzerat',
  beforeLoad: requireAuth,
  component: InzeratPickerPage,
});

const inzeratRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/zakazky/$id/inzerat',
  beforeLoad: requireAuth,
  component: function InzeratRoute() {
    const { id } = inzeratRoute.useParams();
    return <InzeratPage caseId={id} />;
  },
});

/** Detail zakazky (`cases.id`) — jedina implementace detailu (R4, ROZHODNUTI S3). */
// Krok KAMP2: e-mailová kampaň realitním kancelářím (trychtýř, STOP, strop).
const kampaneRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/kampane',
  beforeLoad: requireAuth,
  component: KampanePage,
});

// Krok AKV1: akvizicni radar — bez napojeni sedy stav „Připravujeme" uvnitr stranky.
const akviziceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/akvizice',
  beforeLoad: requireAuth,
  component: AkvizicePage,
});

const zakazkaDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/zakazky/$id',
  beforeLoad: async (opts) => {
    await requireAuth(opts);
    // Krok UXS1 bod 1: editor smlouvy zije na `/smlouvy/:id`. Odkaz na
    // smlouvu uvnitr zakazky (drive jediny zpusob, jak se do editoru dostat)
    // nesmi umrit — vede na adresu editoru.
    const contractId = (opts.search as { smlouva?: string } | undefined)?.smlouva;
    if (typeof contractId === 'string' && contractId !== '') {
      throw redirect({ to: '/smlouvy/$id', params: { id: contractId } });
    }
  },
  component: function ZakazkaDetailRoute() {
    const { id } = zakazkaDetailRoute.useParams();
    const { sekce } = zakazkaDetailRoute.useSearch();
    return <ZakazkaDetailPage caseId={id} {...(sekce !== undefined ? { section: sekce } : {})} />;
  },
  validateSearch: validateCaseSearch,
});

/**
 * Krok QAFIX2 — `/import` je PRUVODCE PREVODU KANCELARE, ne stranka
 * „pripravuje se". Cesta `/import-migrace` (PREPARING_PATHS) zustava, kam
 * patrila: je to budouci sekce menu pro migrace obecne, zatim bez obsahu.
 */
const importRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/import',
  beforeLoad: requireAuth,
  component: ImportPage,
});

const portalyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portaly',
  beforeLoad: requireAuth,
  component: PortalyPage,
});

/**
 * Redirecty ze starych cest. Odkazy a zalozky nesmi umrit:
 *   - terminologie „pripad" → „zakazka" (BLOK 2),
 *   - CRM → Klienti (BLOK 2.5),
 *   - krok S3: `/dashboard`, `/dnesek`, `/prehled-dne` sloucene do Dneska;
 *     `/zakazka*` (fixture cockpit) a `/nemovitost` (property master)
 *     nahrazeny detailem zakazky, resp. seznamem nemovitosti,
 *   - krok C2: `/` uz nepatri administraci (koren domeny je verejna stranka,
 *     apps/web-public), takze vede na `/prehled` — kdo ma zalozku na `/`
 *     a trefi se do web-adminu, skonci na Dnesku, ne na 404.
 */
const REDIRECTS: readonly { readonly from: string; readonly to: string }[] = [
  { from: '/', to: '/prehled' },
  { from: '/dashboard', to: '/prehled' },
  { from: '/dnesek', to: '/prehled' },
  { from: '/prehled-dne', to: '/prehled' },
  { from: '/pripady', to: '/zakazky' },
  { from: '/crm', to: '/klienti' },
  { from: '/pripad', to: '/zakazky' },
  { from: '/pripad/r3', to: '/zakazky' },
  { from: '/zakazka', to: '/zakazky' },
  { from: '/zakazka/r3', to: '/zakazky' },
  { from: '/nemovitost', to: '/nemovitosti' },
  // krok UXP4c (P3): URL anglicky -> cesky, stara zalozka nesmi umrit.
  { from: '/approvals', to: '/schvaleni' },
];

const redirectRoutes = REDIRECTS.map(({ from, to }) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path: from,
    beforeLoad: () => {
      throw redirect({ to });
    },
  }),
);

const preparingRoutes = PREPARING_PATHS.map((path) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path,
    beforeLoad: requireAuthAndSkipPreparing,
    component: function PreparingRoute() {
      return <PreparingPage path={path} />;
    },
  }),
);

/** Exportovano pro testy (router.spec.ts) — vlastni router instance s memory history. */
export const routeTree = rootRoute.addChildren([
  prehledRoute,
  loginRoute,
  resetPasswordRoute,
  resetPasswordConfirmRoute,
  invitationAcceptRoute,
  auditRoute,
  approvalsRoute,
  zakazkyRoute,
  zakazkyNovyRoute,
  naborRoute,
  zakazkaDetailRoute,
  inzeratRoute,
  inzeratPickerRoute,
  klientiRoute,
  poptavkyRoute,
  ukolyRoute,
  kalendarRoute,
  prohlidkaRoute,
  prohlidkyRoute,
  parovaniPoptavekRoute,
  nakladyRoute,
  nakladyInzerceRoute,
  projektyRoute,
  kampaneRoute,
  akviziceRoute,
  neprirazeneNakladyRoute,
  fakturaceRoute,
  spravaBytuRoute,
  ekonomikaKancelareRoute,
  smlouvyRoute,
  smlouvaNovaRoute,
  smlouvaDetailRoute,
  smlouvySablonyRoute,
  elektronickePodpisyRoute,
  reportyRoute,
  administraceRoute,
  objednavkyFrontaRoute,
  kDoplneniRoute,
  nastaveniRoute,
  asistentkaRoute,
  coChystameRoute,
  napovedaRoute,
  tymRoute,
  tarifRoute,
  aiSuperadminRoute,
  provozSuperadminRoute,
  nemovitostiListRoute,
  nemovitostDetailRoute,
  portalyRoute,
  importRoute,
  ...redirectRoutes,
  ...preparingRoutes,
]);

/**
 * Krok UXK1 bod 2 — stranky, ktere MUSI mit spolecnou hlavicku (`PageHead`,
 * `components/page-head.tsx`). Seznam komponenta je TADY, vedle definic
 * rout, ne rucne prepsany v testu (`page-header-coverage.spec.tsx`) — ten
 * jen cte tuto tabulku a overuje, ze komponenta `PageHead` vykresluje.
 *
 * Cesta jde jako STRING LITERAL (ne `route.path` — TanStack Router ho na
 * typove urovni nevystavuje jako citelnou vlastnost `RouteOptions`, viz
 * `page-header-coverage.spec.tsx`), ale radek vedle ni je TATAZ komponenta,
 * jakou dostava `createRoute` o par radku vyse — zadna druha kopie pravdy.
 *
 * Smlouvy: `SmlouvyPage` (routes/smlouvy.tsx) je tenky obal nad
 * `ContractsScreen` (Krok 0 — SSOT hlavicky uz zije tam), proto je tu
 * primo `ContractsScreen`.
 */
export const PAGE_HEADER_ROUTES: readonly {
  readonly path: string;
  readonly component: (...args: never[]) => React.JSX.Element;
}[] = [
  { path: '/prehled', component: DnesekPage },
  { path: '/zakazky', component: ZakazkyPage },
  { path: '/klienti', component: KlientiPage },
  { path: '/nemovitosti', component: NemovitostiPage },
  { path: '/kalendar', component: KalendarPage },
  { path: '/poptavky', component: PoptavkyPage },
  { path: '/portaly', component: PortalyPage },
  { path: '/schvaleni', component: ApprovalsPage },
  { path: '/smlouvy', component: ContractsScreen },
  { path: '/fakturace', component: FakturacePage },
  { path: '/nastaveni', component: NastaveniPage },
  { path: '/ekonomika-kancelare', component: EkonomikaKancelarePage },
  { path: '/asistentka', component: AsistentkaPage },
  { path: '/ukoly', component: UkolyPage },
  { path: '/prohlidky', component: ProhlidkyPage },
  { path: '/reporty', component: ReportyPage },
  { path: '/import', component: ImportPage },
];

/*
  Krok UXP1 (nalez P1-3): bez `defaultErrorComponent` kreslil router vlastni
  anglicke „Something went wrong!" a cela administrace zmizela. Ted zustane
  rozvrzeni i navigace a chyba ma ceskou hlasku s akci.
*/
export const router = createRouter({
  routeTree,
  defaultErrorComponent: ({ error, reset }) => <AppErrorScreen error={error} reset={reset} />,
  // Bod 21 CO-HNED (A8): `/tym`, `/napoveda` a preklepy koncily holym anglickym „Not Found“.
  defaultNotFoundComponent: () => <AppNotFoundScreen />,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
